import { WorkerStage } from "@clipboard-health/contract-worker-app-bff";
import { Alert } from "@mui/material";
import { PullToRefresh } from "@src/appV2/lib/PullToRefresh/PullToRefresh";
import { useEventCallback } from "@src/appV2/lib/utils/useEventCallback";
import { useSearchParams } from "@src/appV2/lib/utils/useSearchParams";
import { PageWrapper } from "@src/appV2/redesign/components/PageWrapper";
import { StickyScrollPageHeader } from "@src/appV2/redesign/components/StickyScrollPageHeader";
import { useIsInHomeEnabled } from "@src/appV2/redesign/InHome/lib/useIsInHomeEnabled";
import { useWorkerPendingVisitInvites } from "@src/appV2/redesign/InHome/VisitInvites/api/useWorkerPendingVisitInvites";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";

import { PageContent } from "../components/PageContent";
import { useWorkerPendingShiftInvites } from "./api/useWorkerPendingShiftInvites";
import { ShiftInvitesEmptyState } from "./EmptyState";
import { ShiftInvitesList } from "./List";
import { ShiftInvitesLoadingPageState } from "./LoadingPageState";
import { MixedInvitesList } from "./MixedInvitesList";

export function ShiftInvitesPage() {
  const { id } = useSearchParams<{ id?: string }>();
  const targetShiftInviteId = typeof id === "string" && id ? id : undefined;

  const [highlightedInviteId, setHighlightedInviteId] = useState<string | undefined>(
    targetShiftInviteId
  );
  const [scrollRequestId, setScrollRequestId] = useState<string | undefined>();

  const history = useHistory();

  useEffect(() => {
    if (targetShiftInviteId) {
      setHighlightedInviteId(targetShiftInviteId);
      setScrollRequestId(`${targetShiftInviteId}-${Date.now()}`);
    }
  }, [targetShiftInviteId]);

  const clearHighlightTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>();

  useEffect(() => {
    return () => {
      if (clearHighlightTimeoutRef.current) {
        clearTimeout(clearHighlightTimeoutRef.current);
      }
    };
  }, []);

  const handleScrollComplete = useEventCallback(() => {
    const params = new URLSearchParams(history.location.search);
    params.delete("id");
    history.replace({ search: params.toString() });
    // Clear highlight state after the border animation (2s) finishes,
    // so repeated deeplinks produce a clean false→true transition.
    clearHighlightTimeoutRef.current = setTimeout(() => {
      setHighlightedInviteId(undefined);
      setScrollRequestId(undefined);
    }, 2000);
  });

  const worker = useDefinedWorker();
  const hideBrowseShiftsButton = worker.stage === WorkerStage.RESTRICTED;

  const {
    data: shiftInvitesData,
    refetch: refetchShiftInvites,
    isLoading: isShiftInvitesLoading,
    isError: isShiftInvitesError,
    isSuccess: isShiftInvitesSuccess,
  } = useWorkerPendingShiftInvites();

  const isInHomeEnabled = useIsInHomeEnabled();

  const pendingShiftInvites = shiftInvitesData?.data ?? [];

  const {
    data: visitInvitesData,
    refetch: refetchVisitInvites,
    isSuccess: isVisitInvitesSuccess,
    isLoading: isVisitInvitesLoading,
  } = useWorkerPendingVisitInvites({
    options: {
      enabled: isInHomeEnabled,
    },
  });

  const pendingVisitInvites = isVisitInvitesSuccess ? (visitInvitesData?.data ?? []) : [];

  const isLoading = isShiftInvitesLoading || (isVisitInvitesLoading && isInHomeEnabled);
  const isSuccess = isShiftInvitesSuccess && (!isInHomeEnabled || isVisitInvitesSuccess);

  // If the deeplinked invite isn't in the loaded data, clean up the URL and state
  // so a stale ?id= param doesn't persist across visits.
  useEffect(() => {
    if (!isSuccess || !targetShiftInviteId || !scrollRequestId) {
      return;
    }
    const inviteExists = (shiftInvitesData?.data ?? []).some(
      (invite) => invite.id === targetShiftInviteId
    );
    if (!inviteExists) {
      handleScrollComplete();
    }
  }, [isSuccess, targetShiftInviteId, scrollRequestId, shiftInvitesData, handleScrollComplete]);

  const scrollRef = useRef<HTMLDivElement>(null);

  function renderInvitesList() {
    const hasAnyInvites = pendingShiftInvites.length > 0 || pendingVisitInvites.length > 0;

    if (!hasAnyInvites) {
      return <ShiftInvitesEmptyState hideBrowseShiftsButton={hideBrowseShiftsButton} />;
    }

    if (pendingVisitInvites.length > 0) {
      return (
        <MixedInvitesList
          shiftInvites={pendingShiftInvites}
          visitInvites={pendingVisitInvites}
          targetShiftInviteId={highlightedInviteId}
          scrollRequestId={scrollRequestId}
          onScrollComplete={handleScrollComplete}
        />
      );
    }

    return (
      <ShiftInvitesList
        shiftInvites={pendingShiftInvites}
        targetShiftInviteId={highlightedInviteId}
        scrollRequestId={scrollRequestId}
        onScrollComplete={handleScrollComplete}
      />
    );
  }

  const title = pendingVisitInvites.length > 0 ? "Invites" : "Shift invites";
  return (
    <PageWrapper>
      <StickyScrollPageHeader title={title} isLoading={isLoading} scrollContainerRef={scrollRef} />

      <PageContent ref={scrollRef}>
        <PullToRefresh
          onRefresh={async () => {
            await Promise.allSettled([refetchShiftInvites(), refetchVisitInvites()]);
          }}
        />
        {isLoading && <ShiftInvitesLoadingPageState />}
        {isShiftInvitesError && <Alert severity="error">Error loading shift invites.</Alert>}
        {isSuccess && renderInvitesList()}
      </PageContent>
    </PageWrapper>
  );
}
